import React, { useEffect, useRef, useState } from 'react';
import { getScrollParent } from '../../AIEmployees/useReveal';
import { Button } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import './LinearProductHero.css';

/**
 * LinearProductHero — §4.2. Interactive hero simulator.
 *
 * Replaces the static image. A stylized Linear-style issue card that the user
 * can interact with:
 *   1. Default: issue with "Assign to…" pill showing the agents dropdown idea
 *   2. On click of @CellCog: animated thought stream → response card
 *   3. After sequence: "Try this in your Linear workspace →" CTA fades in
 *
 * Demonstrates CellCog's power without a signup. Prime real estate used as
 * interactive showcase rather than static image.
 */

// CellCog is first — it's THE differentiated agent. Other agents listed after
// for context, not competition.
const AGENTS = [
  { id: 'cellcog', name: '@CellCog', kind: 'Multimodal' },
  { id: 'cursor', name: '@Cursor', kind: 'Coding' },
  { id: 'devin', name: '@Devin', kind: 'Coding' },
  { id: 'factory', name: '@Factory', kind: 'Coding' },
];

const THOUGHTS = [
  'Reading issue context…',
  'Querying Linear GraphQL for attachments…',
  'Researching top 5 competitors…',
  'Synthesizing findings into comparison PDF…',
  'Building pricing matrix dashboard…',
];

const ARTIFACTS = [
  { icon: '📄', name: 'Competitor_Analysis_Q3.pdf', size: '2.4 MB' },
  { icon: '📊', name: 'Pricing_Matrix_Dashboard.html', size: 'Live' },
];

/* eslint-disable no-unused-vars */
const STAGE = {
  IDLE: 'idle',
  DROPDOWN: 'dropdown',
  THINKING: 'thinking',
  DONE: 'done',
};
/* eslint-enable no-unused-vars */

const LinearProductHero = ({ onConnect }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState('idle');
  const [thoughtIndex, setThoughtIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { root: getScrollParent(ref.current), threshold: 0, rootMargin: '0px 0px 100% 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  // Auto-play the thinking sequence after a short delay when stage→'thinking'
  useEffect(() => {
    if (stage !== 'thinking') return undefined;
    if (thoughtIndex >= THOUGHTS.length) {
      const t = setTimeout(() => setStage('done'), 500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setThoughtIndex((i) => i + 1), 900);
    return () => clearTimeout(t);
  }, [stage, thoughtIndex]);

  const handleSelectAgent = (agentId) => {
    setDropdownOpen(false);
    if (agentId === 'cellcog') {
      setTimeout(() => {
        setStage('thinking');
        setThoughtIndex(0);
      }, 220);
    }
  };

  const handleReset = () => {
    setStage('idle');
    setThoughtIndex(0);
    setDropdownOpen(false);
  };

  const showThoughts = stage === 'thinking' || stage === 'done';
  const visibleThoughts = showThoughts
    ? THOUGHTS.slice(0, Math.min(thoughtIndex + 1, THOUGHTS.length))
    : [];
  const showResponse = stage === 'done';

  return (
    <section className="linear-product-hero" ref={ref}>
      <div
        className={`linear-product-hero__inner reveal ${
          visible ? 'is-visible' : ''
        }`}
      >
        <div
          className={`lph-simulator stage-${stage} ${
            dropdownOpen ? 'dropdown-open' : ''
          }`}
          role="region"
          aria-label="Interactive Linear task simulator"
        >
          {/* Window chrome */}
          <div className="lph-chrome">
            <span className="lph-chrome-dot red" />
            <span className="lph-chrome-dot yellow" />
            <span className="lph-chrome-dot green" />
            <span className="lph-chrome-title">
              ENG-247 · Competitive positioning research
            </span>
          </div>

          {/* Issue body */}
          <div className="lph-body">
            <div className="lph-issue">
              <div className="lph-issue-meta">
                <span className="lph-issue-pill lph-issue-pill--status">
                  In Progress
                </span>
                <span className="lph-issue-pill lph-issue-pill--label">
                  research
                </span>
                <span className="lph-issue-pill lph-issue-pill--label">
                  Q3
                </span>
              </div>
              <h3 className="lph-issue-title">
                Research our top 5 competitors and write a comparison PDF
              </h3>
              <p className="lph-issue-desc">
                Need a deep-dive competitive analysis with positioning map and
                pricing matrix. Attach the output to this ticket when done.
              </p>

              {/* Assignee row */}
              <div className="lph-assignee-row">
                <span className="lph-assignee-label">Assignee</span>
                <div className="lph-assignee-control">
                  <button
                    type="button"
                    className="lph-assignee-trigger"
                    onClick={() => setDropdownOpen((v) => !v)}
                    disabled={stage !== 'idle'}
                  >
                    {stage === 'idle' ? (
                      <>
                        <span className="lph-assignee-avatar lph-assignee-avatar--empty">
                          ?
                        </span>
                        <span>Assign to…</span>
                      </>
                    ) : (
                      <>
                        <span className="lph-assignee-avatar lph-assignee-avatar--cellcog">
                          C
                        </span>
                        <span>@CellCog</span>
                      </>
                    )}
                  </button>

                  {dropdownOpen && (
                    <div className="lph-dropdown" role="listbox">
                      <div className="lph-dropdown-header">Agents</div>
                      {AGENTS.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className={`lph-dropdown-item ${
                            a.id === 'cellcog' ? 'is-featured' : ''
                          }`}
                          onClick={() => handleSelectAgent(a.id)}
                        >
                          <span
                            className={`lph-assignee-avatar ${
                              a.id === 'cellcog'
                                ? 'lph-assignee-avatar--cellcog'
                                : 'lph-assignee-avatar--muted'
                            }`}
                          >
                            {a.id === 'cellcog' ? 'C' : a.name.charAt(1).toUpperCase()}
                          </span>
                          <span className="lph-dropdown-item-name">{a.name}</span>
                          <span className="lph-dropdown-item-kind">{a.kind}</span>
                          {a.id === 'cellcog' && (
                            <span className="lph-dropdown-item-badge">
                              Multimodal
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {stage === 'idle' && !dropdownOpen && (
                <p className="lph-hint">
                  <span className="lph-hint-dot" />
                  <span className="lph-hint-text">
                    <strong>Click here</strong> to assign this issue to an agent
                  </span>
                </p>
              )}
            </div>

            {/* Agent session panel */}
            <div
              className={`lph-agent-panel ${
                stage === 'idle' ? 'is-empty' : ''
              }`}
            >
              <div className="lph-agent-panel-header">
                <span className="lph-agent-dot" />
                <span>Agent Session</span>
                {stage === 'thinking' && (
                  <span className="lph-agent-status">
                    Working…
                  </span>
                )}
                {stage === 'done' && (
                  <span className="lph-agent-status lph-agent-status--done">
                    ✓ Complete
                  </span>
                )}
              </div>

              <div className="lph-agent-body">
                {stage === 'idle' && (
                  <div className="lph-agent-empty">
                    <div className="lph-agent-empty-icon" />
                    <p>
                      CellCog&rsquo;s progress will stream here once you assign
                      the task.
                    </p>
                  </div>
                )}

                {visibleThoughts.map((t, i) => (
                  <div
                    key={i}
                    className={`lph-thought ${
                      i === visibleThoughts.length - 1 && stage === 'thinking'
                        ? 'is-active'
                        : ''
                    }`}
                  >
                    <span className="lph-thought-dot" />
                    <span>{t}</span>
                  </div>
                ))}

                {showResponse && (
                  <div className="lph-response">
                    <div className="lph-response-title">Response</div>
                    {ARTIFACTS.map((a) => (
                      <div key={a.name} className="lph-artifact">
                        <span className="lph-artifact-icon">{a.icon}</span>
                        <span className="lph-artifact-name">{a.name}</span>
                        <span className="lph-artifact-size">{a.size}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Post-interaction CTA overlay */}
          {stage === 'done' && (
            <div className="lph-cta-overlay">
              <div className="lph-cta-content">
                <span className="lph-cta-eyebrow">
                  Connect your Linear workspace
                </span>
                <h3 className="lph-cta-headline">Try this on a real issue.</h3>
                <div className="lph-cta-buttons">
                  <Button
                    type="primary"
                    size="large"
                    className="lph-cta-primary"
                    onClick={onConnect}
                    data-msc-suppress
                  >
                    Assign your first task
                    <ArrowRightOutlined />
                  </Button>
                  <button
                    type="button"
                    className="lph-cta-reset"
                    onClick={handleReset}
                  >
                    Replay
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LinearProductHero;
